import React, { useEffect, useRef, useState } from 'react';

/**
 * Hook to detect screen size breakpoints
 * Returns 'mobile', 'tablet', or 'desktop' based on Tailwind breakpoints
 */
const useScreenSize = () => {
  const [screenSize, setScreenSize] = useState(() => {
    if (typeof window === 'undefined') return 'desktop';
    // Tailwind breakpoints: sm: 640px, lg: 1024px
    if (window.innerWidth < 640) return 'mobile';
    if (window.innerWidth < 1024) return 'tablet';
    return 'desktop';
  });

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setScreenSize('mobile');
      } else if (window.innerWidth < 1024) {
        setScreenSize('tablet');
      } else {
        setScreenSize('desktop');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return screenSize;
};

/**
 * AdsterraMobileAd component - 300x250 for small mobile devices
 * Adsterra Medium Rectangle ad implementation
 */
const AdsterraMobileAd = () => {
  const containerRef = useRef(null);
  
  useEffect(() => {
    if (!containerRef.current) return;
    
    // Clear any existing scripts first
    const container = containerRef.current;
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }
    
    // Set atOptions directly on window right before script loads
    window.atOptions = {
      'key': 'a3d7138938a709109622e5d089ad5fae',
      'format': 'iframe',
      'height': 250,
      'width': 300,
      'params': {}
    };
    
    // Create invoke script
    const invokeScript = document.createElement('script');
    invokeScript.type = 'text/javascript';
    invokeScript.src = 'https://www.highperformanceformat.com/a3d7138938a709109622e5d089ad5fae/invoke.js';
    invokeScript.async = true;
    container.appendChild(invokeScript);
    
    // Cleanup function
    return () => {
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
      // Clean up atOptions
      delete window.atOptions;
    };
  }, []);
  
  return <div ref={containerRef} className="w-full h-full flex items-center justify-center" />;
};

/**
 * AdsterraTabletAd component - 320x50 for tablets
 * Adsterra Mobile Banner ad implementation
 */
const AdsterraTabletAd = () => {
  const containerRef = useRef(null);
  
  useEffect(() => {
    if (!containerRef.current) return;
    
    // Clear any existing scripts first
    const container = containerRef.current;
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }
    
    // Set atOptions directly on window right before script loads
    window.atOptions = {
      'key': '3152b41433325060a47faeaf1f90deec',
      'format': 'iframe',
      'height': 50,
      'width': 320,
      'params': {}
    };
    
    // Create invoke script
    const invokeScript = document.createElement('script');
    invokeScript.type = 'text/javascript';
    invokeScript.src = 'https://www.highperformanceformat.com/3152b41433325060a47faeaf1f90deec/invoke.js';
    invokeScript.async = true;
    container.appendChild(invokeScript);
    
    // Cleanup function
    return () => {
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
      // Clean up atOptions
      delete window.atOptions;
    };
  }, []);
  
  return <div ref={containerRef} className="w-full h-full flex items-center justify-center" />;
};

/**
 * AdsterraBannerAd component - 728x90 for desktop
 * Adsterra Leaderboard banner ad implementation
 */
const AdsterraBannerAd = () => {
  const containerRef = useRef(null);
  
  useEffect(() => {
    if (!containerRef.current) return;
    
    // Clear any existing scripts first
    const container = containerRef.current;
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }
    
    // Set atOptions directly on window right before script loads
    window.atOptions = {
      'key': 'ee11cbfbc2d713659102b8067094518b',
      'format': 'iframe',
      'height': 90,
      'width': 728,
      'params': {}
    };
    
    // Create invoke script
    const invokeScript = document.createElement('script');
    invokeScript.type = 'text/javascript';
    invokeScript.src = 'https://www.highperformanceformat.com/ee11cbfbc2d713659102b8067094518b/invoke.js';
    invokeScript.async = true;
    container.appendChild(invokeScript);
    
    // Cleanup function
    return () => {
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
      // Clean up atOptions
      delete window.atOptions;
    };
  }, []);
  
  return <div ref={containerRef} className="w-full h-full flex items-center justify-center" />;
};

/**
 * ResponsiveAdsterraAd component
 * Conditionally renders the appropriate ad size based on screen breakpoint
 * This prevents all three components from running simultaneously and overwriting window.atOptions
 */
const ResponsiveAdsterraAd = () => {
  const screenSize = useScreenSize();
  
  // Conditionally render only the appropriate ad component
  if (screenSize === 'mobile') {
    return <AdsterraMobileAd />;
  }
  
  if (screenSize === 'tablet') {
    return <AdsterraTabletAd />;
  }
  
  // desktop
  return <AdsterraBannerAd />;
};

export default ResponsiveAdsterraAd;
